import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled, Switch } from "@mui/material";
import FilterHeader from "../FilterHeader";
import TabHeaders from "../TabHeaders";
import styles from "./AgentModels.module.css";
import ModelRow from "./ModelRow";
import { useApi } from "../../../../../../common/hooks";
import AgentCreator from "./AgentCreator";
import { AiAgentsService } from "../../../../../../api";
import { InfiniteList2 } from "../../../../../../common/components";
import { setCurrentDsiAgentSubpage, setIsSuccessAgentsImport } from "../../../../slice/dsi-slice";
import { MODEL_TABS } from "../tabs";
import { translate } from "../../../../../../common/providers";

const MODEL_COLUMN_WIDTHS = {
	id: 50,
	name: 342,
	prompt: 304,
	llm: 304,
};

const ContainedSwitch = styled((props) => (
	<Switch disableRipple focusVisibleClassName=".Mui-focusVisible" {...props} />
))(({ theme }) => ({
	width: 32,
	height: 18,
	padding: 0,
	"& .MuiSwitch-switchBase": {
		padding: 0,
		margin: 0,
		top: "2px",
		left: "2px",
		transform: "translateX(0px)",
		"&.Mui-checked": {
			transform: "translateX(14px)",
			color: "#fff",
			"& + .MuiSwitch-track": {
				backgroundColor: "var(--blue-500)",
				opacity: 1,
				border: 0,
			},
		},
	},
	"& .MuiSwitch-thumb": {
		width: 14,
		height: 14,
		backgroundColor: "#fff",
		boxSizing: "border-box",
	},
	"& .MuiSwitch-track": {
		borderRadius: 9,
		backgroundColor: "#ccc",
		opacity: 1,
		transition: theme.transitions.create(["background-color"], {
			duration: 500,
		}),
	},
}));

export default function AgentModels() {
	const [open, setOpen] = useState("");
	const [count, setCount] = useState(0);
	const [selectedAgent, setSelectedAgent] = useState(null);
	const [sortDirection, setSortDirection] = useState("desc");
	const [sortKey, setSortKey] = useState("");
	const [isLinkedFilter, setIsLinkedFilter] = useState(false);
	const [searchAgentName, setSearchAgentName] = useState("");
	const [infiniteListRequest, setInfiniteListRequest] = useState(null);
	const isSuccessAgentsImport = useSelector(({ dsi }) => dsi.isSuccessAgentsImport);
	const dispatch = useDispatch();
	const { call: getAgentList } = useApi(AiAgentsService.getAgentList);
	const { call: executeAgent } = useApi(AiAgentsService.executeAgent);
	const { call: deleteDefinition } = useApi(AiAgentsService.deleteDefinition);
	useEffect(() => {
		setTimeout(() => {
			dispatch(setCurrentDsiAgentSubpage(0));
		}, 300);
	}, [dispatch]);
	useEffect(() => {
		setInfiniteListRequest(
			() =>
				({ page, limit }) =>
					getAgentList({
						page,
						size: limit,
						direction: `id;${sortDirection.toUpperCase()}`,
						...((searchAgentName?.length > 0 && { titleContains: searchAgentName }) || {}),
						...((isLinkedFilter && { isLinked: isLinkedFilter }) || {}),
					})
		);
	}, [count, searchAgentName, isLinkedFilter, sortDirection, getAgentList]);
	const handleRefresh = () => {
		setCount((prev) => prev + 1);
	};
	useEffect(() => {
		handleRefresh();
		dispatch(setIsSuccessAgentsImport(false));
	}, [isSuccessAgentsImport, dispatch]);
	const handleChangeFilters = ({ name }) => {
		setSearchAgentName(name);
	};
	const handleOpen = () => {
		if (open) {
			setSelectedAgent(null);
		}
		setOpen((prev) => !prev);
	};
	const handleEdit = (agent) => {
		setSelectedAgent(agent);
		handleOpen();
	};
	const handleDeleteAgent = (agentId) => {
		deleteDefinition({ agentId }).then(handleRefresh).catch(console.error);
	};
	const handleLaunch = (payload) => {
		executeAgent(payload).then(handleRefresh).catch(console.error);
	};
	const handleClickSort = (newKey) => {
		if (newKey === sortKey) {
			setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
		} else {
			setSortKey(newKey);
			setSortDirection("asc");
		}
	};
	const rowRenderer = (agent) => (
		<ModelRow
			key={agent.id}
			agent={agent}
			onDelete={handleDeleteAgent}
			onLaunch={handleLaunch}
			onOpenEdition={() => handleEdit(agent)}
			onRefresh={handleRefresh}
		/>
	);
	const handleChangeIsLinkedFilter = () => {
		setIsLinkedFilter((prev) => !prev);
	};
	const additionnalFilter = () => (
		<div className={styles.additionnalFilter}>
			<ContainedSwitch checked={isLinkedFilter} onClick={handleChangeIsLinkedFilter} />
			{translate("dsi.agents.models.filter.link-to-criteria-or-features")}
		</div>
	);
	return (
		<div className={styles.container}>
			<FilterHeader
				actionTitle="dsi.agents.models.new.button"
				additionnalFilter={additionnalFilter}
				onChangeFilters={handleChangeFilters}
				onOpen={handleOpen}
			/>
			<div className={styles.tableHeader}>
				<TabHeaders
					columnWidths={MODEL_COLUMN_WIDTHS}
					sortDirection={sortDirection}
					sortKey={sortKey}
					tabs={MODEL_TABS}
					onClickSort={handleClickSort}
				/>
			</div>
			<div className={styles.list}>
				<InfiniteList2 request={infiniteListRequest} rowRenderer={rowRenderer} />
			</div>
			<AgentCreator agent={selectedAgent} open={open} onClose={handleOpen} onRefresh={handleRefresh} />
		</div>
	);
}
